import { useRouter } from 'next/router';
import queryString from 'query-string';
import { useEffect, useRef } from 'react';

import {
  API_ENDPOINT,
  useClientApiV1AuthSsoRedirectQuery,
} from '../../resources/@types/accountSchema';
import { antmanFetcher } from '../../resources/Fetcher';
import { useReferUrlNavigation } from '../hooks/useReferUrlNavigation';

const MAX_DELAY_MS = 500;

function useClientAuthSsoRedirectQuery(
  options?: Parameters<typeof useClientApiV1AuthSsoRedirectQuery>[0]['options'],
  isFirst = false,
) {
  const { getReferUrl, navigateMainUrl } = useReferUrlNavigation();
  const router = useRouter();
  const antmanTriggerRef = useRef<Promise<unknown> | null>(null);

  const { data, isSuccess, isError } = useClientApiV1AuthSsoRedirectQuery({
    queryKey: [API_ENDPOINT.CLIENT_V1_AUTH_SSO_REDIRECT],
    url: queryString.stringifyUrl({
      url: API_ENDPOINT.CLIENT_V1_AUTH_SSO_REDIRECT,
      query: {
        ...router.query,
        referUrl: getReferUrl(isFirst),
      },
    }),
    options,
  });

  useEffect(() => {
    if (!isFirst && isError) {
      navigateMainUrl();
    }
  }, [isError, isFirst, navigateMainUrl]);

  // 신규 가입 플로우(isFirst) SSO redirect 직전에 ant-man 전역 미들웨어를 프라이밍한다.
  // checkUserDiscount 미들웨어가 SIGNUP UserDiscount를 INSERT해야 redirect 후 강의 상세의
  // paymentInfo가 첫 조회부터 할인가를 응답할 수 있다.
  useEffect(() => {
    if (isFirst && options?.enabled && !antmanTriggerRef.current) {
      antmanTriggerRef.current = antmanFetcher
        .post('/trigger-ant-man', undefined)
        .catch(() => undefined);
    }
  }, [isFirst, options?.enabled]);

  useEffect(() => {
    /**
     * @see {@link https://github.com/inflearn/inflearn-ucc-frontend/pull/415}
     * 여러 컴포넌트에서 같은 쿼리를 사용하고 있어 isSuccess 상태가 공유되고 있음 (queryKey가 같음)
     * options.enabled가 false인데도 다른 곳에서 성공할 경우 아래 로직이 실행될 수 있음
     * 따라서 options.enabled가 true인지 확인하는 조건이 필요함
     */
    if (!options?.enabled || !isSuccess || !data) {
      return;
    }

    const doRedirect = () => {
      location.href = data.redirectUrl;
    };

    if (!isFirst || !antmanTriggerRef.current) {
      doRedirect();
      return;
    }

    // ant-man trigger가 완료되면 redirect를 진행하되, MAX_DELAY_MS를 통해 최대 지연 시간을 제한한다
    let settled = false;
    const go = () => {
      if (settled) {
        return;
      }
      settled = true;
      doRedirect();
    };
    const timer = setTimeout(go, MAX_DELAY_MS);
    antmanTriggerRef.current.finally(() => {
      clearTimeout(timer);
      go();
    });
  }, [isSuccess, data, options?.enabled, isFirst]);
}

useClientAuthSsoRedirectQuery.queryKey = [API_ENDPOINT.CLIENT_V1_AUTH_SSO_REDIRECT];

export default useClientAuthSsoRedirectQuery;
